import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faPlus, faTrash, faRocket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';

import { AWS_BRAND_SYNC_CREATE_AND_EXECUTE, BRANDS_FOR_DROPDOWN } from 'lib/networking/endpoints';
import { backgroundGrey1, greyBorder, danger } from 'lib/css/colors';
import BrandSearchSelect from 'components/aws_brand_sync/BrandSearchSelect';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

function AddBrandSyncModal({ show, onHide, onSuccess }) {
  const [syncName, setSyncName] = useState('');
  const [brandMappings, setBrandMappings] = useState([{ id: 1, brand: null, amazon_url: '' }]);
  const [nextId, setNextId] = useState(2);
  const [validationErrors, setValidationErrors] = useState({});

  // Get brands for dropdown
  const { data: { data: { brands: brandsForDropdown = [] } = {} } = {}, loading: brandsLoading } =
    useGet(BRANDS_FOR_DROPDOWN, {});

  // Post hook for creating and executing sync
  const { postData: createSync, loading: syncLoading } = usePost(
    AWS_BRAND_SYNC_CREATE_AND_EXECUTE,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Sync Started',
        text: 'The brand sync has been created and is being processed.',
        timer: 3000,
        showConfirmButton: false,
      });
      resetForm();
      onSuccess();
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.message || 'Failed to create sync. Please try again.',
      });
    },
  );

  const resetForm = () => {
    setSyncName('');
    setBrandMappings([{ id: 1, brand: null, amazon_url: '' }]);
    setNextId(2);
    setValidationErrors({});
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const addNewRow = () => {
    setBrandMappings([...brandMappings, { id: nextId, brand: null, amazon_url: '' }]);
    setNextId(nextId + 1);
  };

  const removeRow = id => {
    if (brandMappings.length > 1) {
      setBrandMappings(brandMappings.filter(mapping => mapping.id !== id));
      // Clear validation errors for removed row
      const newErrors = { ...validationErrors };
      delete newErrors[`brand_${id}`];
      delete newErrors[`url_${id}`];
      setValidationErrors(newErrors);
    }
  };

  const updateMapping = (id, field, value) => {
    setBrandMappings(
      brandMappings.map(mapping => (mapping.id === id ? { ...mapping, [field]: value } : mapping)),
    );
    // Clear validation error when field is updated
    if (validationErrors[`${field}_${id}`]) {
      setValidationErrors({
        ...validationErrors,
        [`${field}_${id}`]: null,
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!syncName.trim()) {
      errors.syncName = 'Sync name is required';
    }

    const validMappings = brandMappings.filter(m => m.brand || m.amazon_url.trim());

    if (validMappings.length === 0) {
      errors.general = 'At least one brand-URL mapping is required';
    }

    brandMappings.forEach(mapping => {
      if (mapping.brand && !mapping.amazon_url.trim()) {
        errors[`url_${mapping.id}`] = 'Amazon URL is required when brand is selected';
      }
      if (!mapping.brand && mapping.amazon_url.trim()) {
        errors[`brand_${mapping.id}`] = 'Brand is required when Amazon URL is provided';
      }
      if (mapping.amazon_url.trim() && !isValidUrl(mapping.amazon_url.trim())) {
        errors[`url_${mapping.id}`] = 'Please enter a valid Amazon URL';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = urlString => {
    try {
      new URL(urlString);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    // Filter out empty rows and format data
    const validMappings = brandMappings
      .filter(m => m.brand && m.amazon_url.trim())
      .map(m => ({
        brand_id: m.brand.value,
        amazon_url: m.amazon_url.trim(),
      }));

    if (validMappings.length === 0) {
      setValidationErrors({
        general: 'At least one complete brand-URL mapping is required',
      });
      return;
    }

    createSync({
      name: syncName.trim(),
      brand_url_mappings: validMappings,
    });
  };

  const brandOptions = brandsForDropdown.map(brand => ({
    value: brand.id,
    label: `${brand.name} (ID: ${brand.id})`,
    name: brand.name,
  }));

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Create New Brand Sync</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Sync Name */}
        <Form.Group className="mb-4">
          <Form.Label className="fw-bold">Sync Name *</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter a name for this sync (e.g., 'Q1 2026 Brand Updates')"
            value={syncName}
            onChange={e => {
              setSyncName(e.target.value);
              if (validationErrors.syncName) {
                setValidationErrors({ ...validationErrors, syncName: null });
              }
            }}
            isInvalid={!!validationErrors.syncName}
          />
          <Form.Control.Feedback type="invalid">{validationErrors.syncName}</Form.Control.Feedback>
        </Form.Group>

        {/* General Error */}
        {validationErrors.general && (
          <Alert variant="danger" className="mb-3">
            {validationErrors.general}
          </Alert>
        )}

        {/* Brand-URL Mappings Table */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Form.Label className="fw-bold mb-0">Brand-URL Mappings *</Form.Label>
            <Button variant="outline-primary" size="sm" onClick={addNewRow}>
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Add Row
            </Button>
          </div>

          <div className={css(styles.tableContainer)}>
            <Table bordered className="mb-0">
              <thead className={css(styles.tableHeader)}>
                <tr>
                  <th style={{ width: '40%' }}>Brand</th>
                  <th style={{ width: '50%' }}>Amazon URL</th>
                  <th style={{ width: '10%' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {brandMappings.map(mapping => (
                  <tr key={mapping.id}>
                    <td>
                      <BrandSearchSelect
                        value={mapping.brand}
                        options={brandOptions}
                        onChange={selected => updateMapping(mapping.id, 'brand', selected)}
                        isLoading={brandsLoading}
                        isInvalid={!!validationErrors[`brand_${mapping.id}`]}
                        placeholder="Search and select brand..."
                      />
                      {validationErrors[`brand_${mapping.id}`] && (
                        <div className={css(styles.errorText)}>
                          {validationErrors[`brand_${mapping.id}`]}
                        </div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        type="url"
                        placeholder="https://amazon.com/brand-page"
                        value={mapping.amazon_url}
                        onChange={e => updateMapping(mapping.id, 'amazon_url', e.target.value)}
                        isInvalid={!!validationErrors[`url_${mapping.id}`]}
                      />
                      {validationErrors[`url_${mapping.id}`] && (
                        <div className={css(styles.errorText)}>
                          {validationErrors[`url_${mapping.id}`]}
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeRow(mapping.id)}
                        disabled={brandMappings.length === 1}
                        title="Remove row"
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="text-muted mt-2" style={{ fontSize: '0.875rem' }}>
            Add brands and their corresponding Amazon URLs for scraping. At least one complete
            mapping is required.
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={syncLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={syncLoading}
          className="d-flex align-items-center gap-2"
        >
          {syncLoading ? (
            <>
              <Spinner animation="border" size="sm" />
              Creating...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faRocket} className="mr-1" />
              Sync with Scraper Service
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

AddBrandSyncModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  tableContainer: {
    border: `1px solid ${greyBorder}`,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: backgroundGrey1,
  },
  errorText: {
    color: danger,
    fontSize: '0.75rem',
    marginTop: '0.25rem',
  },
});

export default AddBrandSyncModal;
